import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { systemLogin, systemMe, systemLogout } from "../services/systemAuthService";
import { getAdminToken, clearAdminToken } from "../services/api";

// Auth state for the back-office (system-user / RBAC) area only. The public
// member flow is OTP/session-based and does NOT use this context.
//
// Shape held here:
//   user        -> { id, name, email, roles:[slug,...], permissions:[], is_active }
//   permissions -> string[] of permission names granted to the user's role(s)
//   role        -> humanized display name of the user's first role ("Super Admin", ...)
//   loading     -> true while we re-hydrate from a stored token on first load
//
// Note: the backend returns `roles` as an array of slugs, not a single
// `role: {id, name}` object — isSuperAdmin/role below are derived from that
// array. The top role's slug is "admin" (see SFMIS-Backend's
// SuperAdminRoleSeeder), but isSuperAdmin also accepts "super-admin" in case
// an environment names it that way instead — they're the same tier, not two
// separate roles.
//
// Security note: this gates the UI for usability only. The backend re-checks
// every permission on every request — it is the real authority.

// Turn a role slug ("super-admin") into a display label ("Super Admin").
const formatRoleName = (slug) =>
  String(slug || "")
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Only spend a loading cycle if there's a token worth validating.
  const [loading, setLoading] = useState(() => Boolean(getAdminToken()));

  // On mount, if a token is present, validate it and hydrate the user. A dead
  // token throws (401) — the api interceptor clears it; we just drop the user.
  useEffect(() => {
    let active = true;
    if (!getAdminToken()) {
      setLoading(false);
      return;
    }
    systemMe()
      .then((u) => active && setUser(u))
      .catch(() => {
        clearAdminToken();
        if (active) setUser(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const u = await systemLogin(email, password);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await systemLogout();
    setUser(null);
  }, []);

  // Replaces the held user object without a network round-trip — used after
  // systemChangePassword resolves, so must_change_password flips to false
  // immediately (ProtectedRoute's must-change-password guard reads this).
  const updateUser = useCallback((u) => setUser(u), []);

  const permissions = useMemo(() => user?.permissions ?? [], [user]);
  const roles = useMemo(() => user?.roles ?? [], [user]);
  const role = roles.length ? formatRoleName(roles[0]) : null;
  const isSuperAdmin = useMemo(
    () => roles.some((r) => ["super-admin", "admin"].includes(String(r).toLowerCase())),
    [roles]
  );

  // Permission check used to gate UI. Super Admin always passes (mirrors the
  // backend bypass), so newly added permissions not yet in its set still work.
  // An array means "any of" — for routes the backend accepts under more than
  // one permission (e.g. the shroff desk's search_shroff_payments | view_payments).
  const can = useCallback(
    (permission) => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      if (!permission) return true;
      if (Array.isArray(permission)) return permission.some((p) => permissions.includes(p));
      return permissions.includes(permission);
    },
    [user, isSuperAdmin, permissions]
  );

  const value = useMemo(
    () => ({
      user,
      role,
      permissions,
      loading,
      isAuthenticated: Boolean(user),
      isSuperAdmin,
      login,
      logout,
      updateUser,
      can,
    }),
    [user, role, permissions, loading, isSuperAdmin, login, logout, updateUser, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Internal hook; re-exported from hooks/useAuth.js for the conventional import.
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an <AuthProvider>");
  return ctx;
}

export default AuthContext;
